import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Modal,
  ModalDialog,
  Typography,
} from "@mui/joy";
import { useTranslation } from "react-i18next";
import useMembershipStore from "../stores/membershipStore";
import { AdvancedChip } from "./AdvancedChip";
import { PremiumChip } from "./PremiumChip";

interface PremiumRequiredChipProps {
  size?: "sm" | "md" | "lg";
}

export function PremiumRequiredChip({
  size = "sm",
}: PremiumRequiredChipProps): JSX.Element | null {
  const { t } = useTranslation();
  const isPremium = useMembershipStore((state) => state.isPremium);

  if (isPremium) {
    return <PremiumChip size={size} />;
  }

  return (
    <Chip
      size={size}
      variant="soft"
      color="warning"
      sx={{
        fontSize: size === "sm" ? "0.65rem" : "0.8rem",
        fontWeight: 700,
        px: size === "sm" ? 0.75 : 1,
        py: size === "sm" ? 0.15 : 0.35,
        textTransform: "uppercase",
      }}
    >
      {t("premiumRequired.badge")}
    </Chip>
  );
}

interface PremiumRequiredModalProps {
  open: boolean;
  onClose: () => void;
  onViewMembership: () => void;
}

export function PremiumRequiredModal({
  open,
  onClose,
  onViewMembership,
}: PremiumRequiredModalProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(2px)",
          },
        },
      }}
    >
      <ModalDialog
        sx={{
          width: { xs: "90vw", sm: 360 },
          borderRadius: "lg",
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <Typography level="title-lg" sx={{ flex: 1 }}>
            {t("premiumRequired.modal.title")}
          </Typography>
          <IconButton size="sm" onClick={onClose} sx={{ p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography
          level="body-sm"
          sx={{ color: "text.secondary", mb: 2, textAlign: "left" }}
        >
          {t("premiumRequired.modal.message")}
        </Typography>
        <Button
          variant="solid"
          color="primary"
          size="sm"
          sx={{ mt: 3, width: "100%" }}
          onClick={onViewMembership}
        >
          {t("premiumRequired.modal.viewMembership")}
        </Button>
      </ModalDialog>
    </Modal>
  );
}

interface AdvancedRequiredChipProps {
  size?: "sm" | "md" | "lg";
}

export function AdvancedRequiredChip({
  size = "sm",
}: AdvancedRequiredChipProps): JSX.Element | null {
  const { t } = useTranslation();
  const isAdvancedOrHigher = useMembershipStore(
    (state) => state.isAdvancedOrHigher
  );

  if (isAdvancedOrHigher()) {
    return <AdvancedChip size={size} />;
  }

  return (
    <Chip
      size={size}
      variant="soft"
      color="warning"
      sx={{
        fontSize: size === "sm" ? "0.65rem" : "0.8rem",
        fontWeight: 700,
        px: size === "sm" ? 0.75 : 1,
        py: size === "sm" ? 0.15 : 0.35,
        textTransform: "uppercase",
      }}
    >
      {t("advancedRequired.badge")}
    </Chip>
  );
}

interface AdvancedRequiredModalProps {
  open: boolean;
  onClose: () => void;
  onViewMembership: () => void;
}

export function AdvancedRequiredModal({
  open,
  onClose,
  onViewMembership,
}: AdvancedRequiredModalProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(2px)",
          },
        },
      }}
    >
      <ModalDialog
        sx={{
          width: { xs: "90vw", sm: 360 },
          borderRadius: "lg",
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <Typography level="title-lg" sx={{ flex: 1 }}>
            {t("advancedRequired.modal.title")}
          </Typography>
          <IconButton size="sm" onClick={onClose} sx={{ p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography
          level="body-sm"
          sx={{ color: "text.secondary", mb: 2, textAlign: "left" }}
        >
          {t("advancedRequired.modal.message")}
        </Typography>
        <Button
          variant="solid"
          color="primary"
          size="sm"
          sx={{ mt: 3, width: "100%" }}
          onClick={onViewMembership}
        >
          {t("advancedRequired.modal.viewMembership")}
        </Button>
      </ModalDialog>
    </Modal>
  );
}

interface VoicePointsExhaustedModalProps {
  open: boolean;
  onClose: () => void;
  onViewMembership: () => void;
}

/**
 * Shown when a voice generation is refused because the user has spent every
 * voice point in their daily quota. Mirrors AdvancedRequiredModal so running
 * out of points reads like the other upgrade prompts instead of an error.
 */
export function VoicePointsExhaustedModal({
  open,
  onClose,
  onViewMembership,
}: VoicePointsExhaustedModalProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(2px)",
          },
        },
      }}
    >
      <ModalDialog
        sx={{
          width: { xs: "90vw", sm: 360 },
          borderRadius: "lg",
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <Typography level="title-lg" sx={{ flex: 1 }}>
            {t("voicePointsExhausted.modal.title")}
          </Typography>
          <IconButton size="sm" onClick={onClose} sx={{ p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography
          level="body-sm"
          sx={{ color: "text.secondary", mb: 2, textAlign: "left" }}
        >
          {t("voicePointsExhausted.modal.message")}
        </Typography>
        <Button
          variant="solid"
          color="primary"
          size="sm"
          sx={{ mt: 3, width: "100%" }}
          onClick={onViewMembership}
        >
          {t("voicePointsExhausted.modal.viewMembership")}
        </Button>
      </ModalDialog>
    </Modal>
  );
}
